import type { EntityType, StaticEntityType } from "@config";
import { useSlidesLimit } from "@editor/hooks";
import { useNewSlidesStore } from "@editor/modules/editor/stores/use-new-slides-store";
import { carryOverRichText } from "@editor/utils";
import { store, useAppDispatch } from "@store";
import { getAiSlideContentSlide } from "@store/ai-slides/selectors";
import { changeEntityLayout, createEntity } from "@store/story";
import { getSlideWithEntities } from "@store/story/selectors";
import { useCallback } from "react";
import { invertFlatData, normalizedFromNestedStructured } from "@/shared";
import { resetSlideIds } from "./reset-slide-ids";

const useAddSlides = () => {
  const dispatch = useAppDispatch();
  const { lockAction, wouldExceedLimit } = useSlidesLimit();

  // Returns the root entity id(s) actually used, in the same order as
  // `slides` -- added so a caller that needs to address what it just
  // inserted (e.g. AI add-slide's follow-up chips) doesn't have to guess an
  // id nobody handed back. Every existing caller ignores the return value,
  // so this is additive: same params, same dispatches, same side effects.
  const handleAddSlides = useCallback(
    (
      slides: StaticEntityType[] | null,
      options?: { preserveContentText?: boolean },
    ): string[] => {
      if (!slides) return [];
      // Layout-apply / AI-regenerate carry the current slide's typed text into
      // the incoming slide; content-bearing inserts (saved slides) opt out so
      // their own content survives. Default preserves existing behaviour.
      const preserveContentText = options?.preserveContentText ?? true;

      // Read live: callers dispatch setContentSlide then immediately call
      // this in the same tick; a useAppSelector capture would still hold the
      // previous value and take the wrong branch.
      const contentSlide = getAiSlideContentSlide(store.getState());

      if (contentSlide?.placement === "replace") {
        const state = store.getState();
        const activeEntityId = contentSlide.id;
        const slide = getSlideWithEntities(activeEntityId)(state) as {
          entities: Record<string, EntityType>;
          root: string;
        };
        const inverted = invertFlatData({
          flatData: {
            root: {
              type: "root",
              id: "root",
              settings: {},
              parentId: null,
              childIds: [slide.root],
            },
            ...slide.entities,
          },
        })[0] as StaticEntityType;

        // Positional match: textStyle names diverge between the blank-slide
        // template and AI layouts, so slot index is the only reliable mapping.
        // Saved slides pass preserveContentText=false so their own content is
        // kept instead of being overwritten by the blank slide's placeholders.
        const resetted = carryOverRichText(
          resetSlideIds(slides[0]),
          inverted,
          preserveContentText,
        );

        dispatch(
          changeEntityLayout({
            parentId: "root",
            original: inverted,
            changed: { ...resetted, id: slide.root },
            type: "slide",
          }),
        );

        useNewSlidesStore.getState().markAsSeen(slide.root);

        return [slide.root];
      }

      // Same predicate the AI agent's insert-slide handler now asks
      // (useSlidesLimit's wouldExceedLimit), so the two add paths cannot
      // disagree about the limit. Returning [] is how a caller learns the
      // insert did NOT happen -- see send-chat-message.ts, which used to
      // report "Added ..." regardless.
      if (wouldExceedLimit(slides.length)) {
        lockAction();
        return [];
      }
      // createEntity inserts at anchor_index + 1; iterate in reverse so
      // the final order matches the caller's. insertedRootIds is built the
      // same way (unshift on each reverse step), so it comes out in the
      // same order as `slides` despite the reverse walk.
      const insertedRootIds: string[] = [];
      for (let i = slides.length - 1; i >= 0; i--) {
        const slide = slides[i];
        const normalized = normalizedFromNestedStructured(
          [slide],
          "root",
          "slide",
        );
        dispatch(
          createEntity({
            id: contentSlide?.id,
            entityEntryRootId: normalized.root,
            entities: normalized.entities,
            feature: "slides-library",
            placement: contentSlide?.placement,
          }),
        );
        insertedRootIds.unshift(normalized.root);
      }
      return insertedRootIds;
    },
    [dispatch, lockAction, wouldExceedLimit],
  );

  return { handleAddSlides };
};

export default useAddSlides;

